import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listPrompts } from 'features/prompts/api/promptsApi';

/**
 * A managed-prompt reference on a span or run — name, and the immutable version
 * that actually ran.
 *
 * Prompts are SPAN-level: one run's spans routinely use different prompts,
 * different versions, or none at all.
 *
 * A trace identifies the version by HASH and shows no label. Labels move — the
 * `production` a span followed last week may point somewhere else today — so a
 * label rendered beside a hash reads as "this is production now" and is wrong
 * the moment it moves. The hash is the absolute truth of what ran, and a trace
 * is a record of what happened, not of what is currently deployed.
 *
 * The label the run followed is not lost: it stays on the span as
 * `trodo.prompt.label` and is visible in the raw attributes panel, where it
 * reads as recorded history rather than as current state.
 *
 * Linking needs a prompt ID, and the SDK stamps the NAME (the hash identifies
 * the version, not the prompt row). So the team's prompt list is fetched once
 * and used as a name→id index. When the name resolves we link; when it doesn't
 * — the prompt was renamed or deleted since the run — we still render the name
 * and hash as text, because the historical fact is worth showing even when
 * there is nowhere left to navigate to.
 */

// One in-flight fetch and one cached index per team, shared by every reference
// on the page: a 40-span trace must not make 40 identical requests.
let _indexPromise = null;
let _indexTeam = null;

function promptIndex(teamKey) {
  if (_indexPromise && _indexTeam === teamKey) return _indexPromise;
  _indexTeam = teamKey;
  _indexPromise = listPrompts()
    .then((rows) => {
      const byName = new Map();
      for (const p of rows || []) if (p?.name && p?.id) byName.set(p.name, p.id);
      return byName;
    })
    .catch(() => new Map());   // a failed lookup degrades to plain text, never an error
  return _indexPromise;
}

/** Test seam / cache reset when the team changes. */
export function _resetPromptIndex() {
  _indexPromise = null;
  _indexTeam = null;
}

function getSelectedTeam() {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('selectedTeam') : null;
    return raw || null;
  } catch {
    return null;
  }
}

export default function PromptRefLink({ name, versionHash }) {
  const [promptId, setPromptId] = useState(null);
  const team = getSelectedTeam();

  useEffect(() => {
    if (!name) return undefined;
    let cancelled = false;
    promptIndex(team).then((idx) => { if (!cancelled) setPromptId(idx.get(name) || null); });
    return () => { cancelled = true; };
  }, [name, team]);

  if (!name) return null;

  const shortHash = versionHash ? String(versionHash).slice(0, 8) : null;
  const title = `${name}${shortHash ? ` @ ${shortHash}` : ''}`
    + (promptId ? '' : ' — prompt not found in this workspace');

  // Unlinkable — no team context, or a prompt that no longer exists. Still
  // rendered: the historical fact is worth showing even with nowhere to go.
  if (!promptId || !team) {
    return (
      <span className="inline-flex items-baseline gap-1.5 text-[color:var(--text-secondary)]" title={title}>
        <span className="truncate">{name}</span>
        {shortHash ? (
          <span className="font-mono text-[10px] text-[color:var(--text-muted)]">{shortHash}</span>
        ) : null}
      </span>
    );
  }

  const base = `/${encodeURIComponent(team)}/prompts/${encodeURIComponent(promptId)}`;
  const stop = (e) => e.stopPropagation();

  // Two targets, because they answer different questions. The NAME opens the
  // prompt as it stands today — what is it now, who owns it, what is live. The
  // VERSION opens the exact immutable version this span executed, which is the
  // one that explains the output you are looking at; "latest" may have moved on
  // several times since.
  return (
    <span className="inline-flex items-baseline gap-1.5" title={title}>
      <Link to={base} onClick={stop}
        className="truncate text-[color:var(--text-primary)] underline-offset-2 hover:underline">
        {name}
      </Link>
      {shortHash ? (
        <Link
          to={`${base}?version=${encodeURIComponent(versionHash)}`}
          onClick={stop}
          title={`Open version ${shortHash}: the exact version this ran`}
          className="font-mono text-[10px] text-[color:var(--text-muted)] underline-offset-2 hover:text-[color:var(--text-primary)] hover:underline"
        >
          {shortHash}
        </Link>
      ) : null}
    </span>
  );
}
